// Bills, charges, payments, receipts and cashier shifts (docs/plans/phase1-billing.md §3.3).
// Every write carries a client request key, so a retried payment can't be taken twice.
import { queryOptions } from '@tanstack/react-query';
import {
  adjustmentViewSchema,
  billingApprovalListSchema,
  creditNoteViewSchema,
  refundViewSchema,
  type creditNoteBodySchema,
  type refundBodySchema,
  billingSettingsSchema,
  billListSchema,
  billViewSchema,
  currentShiftSchema,
  issueBillResultSchema,
  priceAgainResultSchema,
  receiptPrintedSchema,
  receiptViewSchema,
  shiftListSchema,
  shiftViewSchema,
  takePaymentResultSchema,
  type adjustmentBodySchema,
  type closeShiftBodySchema,
  type manualChargeBodySchema,
  type takePaymentBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

export const billingKeys = {
  all: ['billing'] as const,
  bill: (visitId: string) => ['billing', 'bill', visitId] as const,
  bills: (state: 'owing' | 'all') => ['billing', 'bills', state] as const,
  shift: ['billing', 'shift', 'current'] as const,
  shifts: ['billing', 'shifts'] as const,
  receipt: (id: string) => ['billing', 'receipt', id] as const,
  settings: ['billing', 'settings'] as const,
};

export const billQuery = (visitId: string) =>
  queryOptions({
    queryKey: billingKeys.bill(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/bill`, { schema: billViewSchema }),
  });

export const billsQuery = (state: 'owing' | 'all') =>
  queryOptions({
    queryKey: billingKeys.bills(state),
    queryFn: () => api('GET', `/bills?state=${state}&limit=100`, { schema: billListSchema }),
  });

export const currentShiftQuery = queryOptions({
  queryKey: billingKeys.shift,
  queryFn: () => api('GET', '/cashier-shifts/current', { schema: currentShiftSchema }),
});

export const shiftsQuery = queryOptions({
  queryKey: billingKeys.shifts,
  queryFn: () => api('GET', '/cashier-shifts?limit=50', { schema: shiftListSchema }),
});

export const receiptQuery = (id: string) =>
  queryOptions({
    queryKey: billingKeys.receipt(id),
    queryFn: () => api('GET', `/receipts/${id}`, { schema: receiptViewSchema }),
  });

export const billingSettingsQuery = queryOptions({
  queryKey: billingKeys.settings,
  queryFn: () => api('GET', '/billing-settings', { schema: billingSettingsSchema }),
});

export const addManualCharge = (visitId: string, body: In<typeof manualChargeBodySchema>) =>
  api('POST', `/visits/${visitId}/charges`, { body, schema: billViewSchema });

export const cancelCharge = (chargeId: string, reason: string) =>
  api('POST', `/charges/${chargeId}/cancel`, { body: { reason }, schema: billViewSchema });

export const priceAgain = (visitId: string) =>
  api('POST', `/visits/${visitId}/charges/price-again`, { schema: priceAgainResultSchema });

export const takePayment = (visitId: string, body: In<typeof takePaymentBodySchema>) =>
  api('POST', `/visits/${visitId}/payments`, { body, schema: takePaymentResultSchema });

export const issueBill = (visitId: string) =>
  api('POST', `/visits/${visitId}/invoices`, { schema: issueBillResultSchema });

export const markReceiptPrinted = (id: string) =>
  api('POST', `/receipts/${id}/printed`, { schema: receiptPrintedSchema });

export const markBillPrinted = (visitId: string) => api('POST', `/visits/${visitId}/bill/printed`);

export const openShift = (openingFloatCents: bigint) =>
  api('POST', '/cashier-shifts', {
    body: { clientRequestId: crypto.randomUUID(), openingFloatCents: openingFloatCents.toString() },
    schema: shiftViewSchema,
  });

export const closeShift = (shiftId: string, body: In<typeof closeShiftBodySchema>) =>
  api('POST', `/cashier-shifts/${shiftId}/close`, { body, schema: shiftViewSchema });

export const saveBillingSettings = (payBefore: string[], expectedVersion: number) =>
  api('PUT', '/billing-settings', {
    body: { payBefore, expectedVersion },
    schema: billingSettingsSchema,
  });

export const approvalsQuery = queryOptions({
  queryKey: ['billing', 'approvals'] as const,
  queryFn: () => api('GET', '/billing-approvals', { schema: billingApprovalListSchema }),
});

export const requestAdjustment = (chargeId: string, body: In<typeof adjustmentBodySchema>) =>
  api('POST', `/charges/${chargeId}/adjustments`, { body, schema: adjustmentViewSchema });

export const decideAdjustment = (
  id: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/charge-adjustments/${id}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: adjustmentViewSchema,
  });

export const requestCreditNote = (invoiceId: string, body: In<typeof creditNoteBodySchema>) =>
  api('POST', `/invoices/${invoiceId}/credit-notes`, { body, schema: creditNoteViewSchema });

export const decideCreditNote = (
  id: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/credit-notes/${id}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: creditNoteViewSchema,
  });

export const requestRefund = (paymentId: string, body: In<typeof refundBodySchema>) =>
  api('POST', `/payments/${paymentId}/refunds`, { body, schema: refundViewSchema });

export const decideRefund = (
  id: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/refunds/${id}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: refundViewSchema,
  });

export const payRefund = (id: string, externalReference?: string) =>
  api('POST', `/refunds/${id}/pay`, {
    body: externalReference === undefined ? {} : { externalReference },
    schema: refundViewSchema,
  });

export const setServicePointChargeItem = (servicePointId: string, serviceItemId: string | null) =>
  api('PUT', `/service-points/${servicePointId}/charge-item`, {
    body: { serviceItemId },
    schema: z.object({ servicePointId: z.uuid(), serviceItemId: z.uuid().nullable() }),
  });
